import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { SERVICES, SERVICE_CATEGORIES } from "../config/site";

export const Services = ({ onBook }) => {
  const [filter, setFilter] = useState("All");
  const visible =
    filter === "All" ? SERVICES : SERVICES.filter((s) => s.category === filter);

  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Overline>Our Services</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-brand-ink tracking-tight leading-tight">
            Nineteen ways to
            <span className="italic text-brand-green"> feel renewed.</span>
          </h2>
          <p className="mt-5 text-brand-mute text-base sm:text-lg leading-relaxed">
            Premium services at honest neighbourhood prices — for women, men,
            brides and everyone in between.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="services-filter"
            className="mt-10 flex flex-wrap gap-2.5"
            role="tablist"
            aria-label="Filter services by category"
          >
            {SERVICE_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                role="tab"
                aria-selected={filter === c}
                onClick={() => setFilter(c)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  filter === c
                    ? "bg-brand-green text-white shadow-soft"
                    : "bg-white text-brand-mute border border-brand-green/15 hover:border-brand-green/40 hover:text-brand-green"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((s, i) => (
              <motion.article
                layout
                key={s.name}
                data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-white rounded-3xl overflow-hidden shadow-soft flex flex-col transition-shadow duration-500 hover:shadow-lift"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    className="w-full h-full object-cover img-warm transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 rounded-full bg-brand-cream/90 backdrop-blur px-3 py-1 text-[11px] font-semibold tracking-wide text-brand-green">
                    {s.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col grow">
                  <h3 className="font-serif text-xl font-semibold text-brand-ink">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-sm text-brand-mute leading-relaxed grow">
                    {s.desc}
                  </p>
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <p className="text-brand-green font-semibold">
                      {s.price}
                      <span className="text-brand-mute text-xs font-normal"> onwards</span>
                    </p>
                    <button
                      data-testid={`book-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => onBook(s.name)}
                      className="rounded-full border border-brand-green px-4 py-2 text-xs font-semibold text-brand-green transition-all duration-300 hover:bg-brand-green hover:text-white"
                      aria-label={`Book appointment for ${s.name}`}
                    >
                      Book Appointment
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-brand-mute">
            Prices are indicative starting prices and may vary by hair length,
            product range and artist. Final quote shared before we begin — no
            surprises, ever.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
